import { Dialog as ChakraDialog, Portal } from '@chakra-ui/react'
import * as React from 'react'

// ==============================================================================
// DIALOG ROOT
// ==============================================================================

export interface DialogRootProps extends ChakraDialog.RootProps {
  // Compatibility props from v2 Modal
  isOpen?: boolean
  onClose?: () => void
  isCentered?: boolean
  closeOnOverlayClick?: boolean
}

export const DialogRoot: React.FC<DialogRootProps> = (props) => {
  const {
    isOpen,
    onClose,
    open,
    onOpenChange,
    isCentered,
    closeOnOverlayClick,
    placement,
    closeOnInteractOutside,
    ...rest
  } = props

  // Handle backward compatibility
  const isOpenCompat = open ?? isOpen
  const handleOpenChange = React.useCallback(
    (details: { open: boolean }) => {
      if (onOpenChange) {
        onOpenChange(details)
      } else if (onClose && !details.open) {
        onClose()
      }
    },
    [onOpenChange, onClose],
  )

  return (
    <ChakraDialog.Root
      open={isOpenCompat}
      onOpenChange={handleOpenChange}
      placement={placement ?? (isCentered ? 'center' : undefined)}
      closeOnInteractOutside={
        closeOnInteractOutside ?? closeOnOverlayClick ?? true
      }
      {...rest}
    />
  )
}

// ==============================================================================
// DIALOG CONTENT
// ==============================================================================

export interface DialogContentProps extends ChakraDialog.ContentProps {
  portalled?: boolean
  portalRef?: React.RefObject<HTMLElement | null>
  backdrop?: boolean
}

export const DialogContent = React.forwardRef<
  HTMLDivElement,
  DialogContentProps
>(function DialogContent(props, ref) {
  const {
    children,
    portalled = true,
    portalRef,
    backdrop = true,
    ...rest
  } = props

  return (
    <Portal disabled={!portalled} container={portalRef}>
      {backdrop && <ChakraDialog.Backdrop />}
      <ChakraDialog.Positioner>
        <ChakraDialog.Content ref={ref} {...rest} asChild={false}>
          {children}
        </ChakraDialog.Content>
      </ChakraDialog.Positioner>
    </Portal>
  )
})

// ==============================================================================
// DIALOG CLOSE TRIGGER
// ==============================================================================

export const DialogCloseTrigger = React.forwardRef<
  HTMLButtonElement,
  ChakraDialog.CloseTriggerProps
>(function DialogCloseTrigger(props, ref) {
  return (
    <ChakraDialog.CloseTrigger
      position="absolute"
      top="2"
      insetEnd="2"
      {...props}
      ref={ref}
      asChild={false}
    />
  )
})

// ==============================================================================
// DIALOG SUB-COMPONENTS
// ==============================================================================

export const DialogBackdrop = ChakraDialog.Backdrop
export const DialogHeader = ChakraDialog.Header
export const DialogBody = ChakraDialog.Body
export const DialogFooter = ChakraDialog.Footer
export const DialogTitle = ChakraDialog.Title
export const DialogDescription = ChakraDialog.Description
export const DialogTrigger = ChakraDialog.Trigger
export const DialogActionTrigger = ChakraDialog.ActionTrigger
export const DialogPositioner = ChakraDialog.Positioner

// ==============================================================================
// BACKWARD COMPATIBILITY EXPORTS (Modal → Dialog)
// ==============================================================================

/**
 * @deprecated Use DialogRoot instead
 * Backward-compatible alias for v2 Modal
 */
export const Modal = DialogRoot

/**
 * @deprecated Use DialogBackdrop instead
 */
export const ModalOverlay = DialogBackdrop

/**
 * @deprecated Use DialogContent instead
 */
export const ModalContent = DialogContent

/**
 * @deprecated Use DialogHeader instead
 */
export const ModalHeader = DialogHeader

/**
 * @deprecated Use DialogBody instead
 */
export const ModalBody = DialogBody

/**
 * @deprecated Use DialogFooter instead
 */
export const ModalFooter = DialogFooter

/**
 * @deprecated Use DialogCloseTrigger instead
 */
export const ModalCloseButton = DialogCloseTrigger

// ==============================================================================
// NAMESPACE EXPORT
// ==============================================================================

export const Dialog = {
  Root: DialogRoot,
  Content: DialogContent,
  CloseTrigger: DialogCloseTrigger,
  Backdrop: DialogBackdrop,
  Header: DialogHeader,
  Body: DialogBody,
  Footer: DialogFooter,
  Title: DialogTitle,
  Description: DialogDescription,
  Trigger: DialogTrigger,
  ActionTrigger: DialogActionTrigger,
  Positioner: DialogPositioner,
}
